import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { v4 as uuidv4 } from 'uuid';
import { saveChunk, saveSession, finalizeSession, RecordingChunk } from './useIndexedDBBackup';
import { useUploadQueue } from './useUploadQueue';
import logger from "utils/logger";

let amplitudeStoreValue = 0;
let amplitudeSubscribers: Set<() => void> = new Set();

export function subscribeAmplitude(callback: () => void) {
  amplitudeSubscribers.add(callback);
  return () => amplitudeSubscribers.delete(callback);
}
export function getAmplitude() {
  return amplitudeStoreValue;
}

export function useSharedAmplitude() {
  return useSyncExternalStore(subscribeAmplitude, getAmplitude);
}

// Chunk interval: 30 seconds
const CHUNK_INTERVAL_MS = 30000;

/**
 * Voice recorder hook with:
 * - Live amplitude tracking (0–1)
 * - Chunked recording every 30s (prevents data loss)
 * - IndexedDB backup for each chunk
 * - Upload queue with retry logic
 * - Support for very long recordings (1-1.5 hours)
 * - Patient association for automatic document linking
 */
export function useVoiceRecorder(
  onSave: (blob: Blob, url: string, name: string) => void,
  patientId?: string
) {
  const [recording, setRecording] = useState(false);
  const [amplitude, setAmplitude] = useState(0); // live mic intensity 0–1
  const [recordingDuration, setRecordingDuration] = useState(0); // seconds
  const [chunksRecorded, setChunksRecorded] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  
  // Chunked recording state
  const sessionIdRef = useRef<string | null>(null);
  const chunkIndexRef = useRef(0);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const localChunksRef = useRef<BlobPart[]>([]); // Local accumulation for immediate use
  const cancelledRef = useRef(false); // set by cancelRecording() to discard on stop

  const uploadQueue = useUploadQueue();

  const startRecording = async () => {
    try {
      // Generate session ID
      const sessionId = uuidv4();
      sessionIdRef.current = sessionId;
      chunkIndexRef.current = 0;
      localChunksRef.current = []; // Reset local accumulation
      setChunksRecorded(0);
      setRecordingDuration(0);

      // CRITICAL: Reset upload queue so uploadedCount starts at 0 for
      // this session.  Without this, waitForUploads() in onstop sees
      // the count from the PREVIOUS recording and returns immediately
      // before new chunks finish uploading → race condition → 404.
      uploadQueue.reset();

      // Save session metadata
      await saveSession({
        sessionId,
        startTime: Date.now(),
        lastChunkIndex: -1,
        totalChunks: 0,
        finalized: false,
        patientId,
      });

      // Ask for mic
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Web Audio for amplitude visualization
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      await audioContext.resume().catch(() => {}); // some browsers need resume()
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      // Animation loop to compute RMS → normalized amplitude
      const data = new Uint8Array(analyser.fftSize);
      let ema = 0; // exponential moving average for smoothness

      const tick = () => {
        analyser.getByteTimeDomainData(data);

        // Compute RMS (0–255 centered at 128)
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const dev = data[i] - 128;
          sum += dev * dev;
        }
        const rms255 = Math.sqrt(sum / data.length);

        // 🔊 Convert RMS to a perceptual loudness curve
        // Typical speech yields rms255 ≈ 3–20 → map that more dynamically
        let norm = Math.min(rms255 / 25, 1); // normalize 0–1
        norm = Math.pow(norm, 0.6); // perceptual boost (less flat)

        // 💨 Smooth with exponential moving average
        ema = ema * 0.6 + norm * 0.4;
        setAmplitude(ema);
        amplitudeStoreValue = ema;
        for (const cb of amplitudeSubscribers) cb();

        rafRef.current = requestAnimationFrame(tick);
      };

      tick();

      // Duration timer (update every second)
      durationTimerRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);

      // MediaRecorder for actual capture with CHUNKED recording
      const recorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
        audioBitsPerSecond: 64000, // speech-friendly size
      });
      mediaRecorderRef.current = recorder;

      // Handle each chunk (every 30 seconds)
      recorder.ondataavailable = async (e) => {
        if (e.data.size > 0) {
          // Accumulate locally for immediate blob creation (needed for Notes mode)
          localChunksRef.current.push(e.data);

          const chunkIndex = chunkIndexRef.current++;
          const chunk: RecordingChunk = {
            sessionId,
            chunkIndex,
            blob: e.data,
            timestamp: Date.now(),
            uploaded: false,
            retryCount: 0,
          };

          try {
            // 1. Save to IndexedDB immediately (offline backup)
            await saveChunk(chunk);
            logger.debug(`[VoiceRecorder] Chunk ${chunkIndex} saved to IndexedDB`);

            // 2. Queue for upload (with retry)
            uploadQueue.enqueue(chunk);
            
            setChunksRecorded((prev) => prev + 1);

            // 3. Update session metadata
            await saveSession({
              sessionId,
              startTime: Date.parse(sessionId.split('-')[0]),
              lastChunkIndex: chunkIndex,
              totalChunks: chunkIndex + 1,
              finalized: false,
              patientId,
            });
          } catch (error) {
            logger.error(`[VoiceRecorder] Failed to save chunk ${chunkIndex}:`, error);
          }
        }
      };

      recorder.onstop = async () => {
        // Cleanup amplitude animation
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        if (durationTimerRef.current) clearInterval(durationTimerRef.current);
        setAmplitude(0);

        // Discard path: cancelRecording() was called — drop everything, no
        // upload wait, no transcription, no onSave callback.
        if (cancelledRef.current) {
          cancelledRef.current = false;
          stream.getTracks().forEach((tr) => tr.stop());
          streamRef.current = null;
          try {
            audioContext.close();
          } catch {}
          audioContextRef.current = null;
          analyserRef.current = null;
          localChunksRef.current = [];
          setRecording(false);
          logger.debug("[VoiceRecorder] Recording cancelled — discarded");
          return;
        }

        const totalChunks = chunkIndexRef.current;
        logger.debug(`[VoiceRecorder] Recording stopped. Total chunks: ${totalChunks}`);

        // Wait for all chunks to finish uploading
        logger.debug('[VoiceRecorder] Waiting for chunks to upload...');
        const uploadSuccess = await uploadQueue.waitForUploads(totalChunks, 60000);
        
        if (!uploadSuccess) {
          logger.error('[VoiceRecorder] Upload timeout - some chunks may not have uploaded');
        }

        logger.debug(`[VoiceRecorder] All ${uploadQueue.getUploadedCount()} chunks uploaded`);

        // Cleanup devices & audio graph
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;

        try {
          audioContext.close();
        } catch {}
        audioContextRef.current = null;
        analyserRef.current = null;

        setRecording(false);

        // Create actual blob from accumulated local chunks
        // This is needed for Notes mode (immediate transcription) 
        // Voice mode uses server-side chunks for finalization
        const blob = new Blob(localChunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        const name = `voice_${sessionId}_${chunkIndexRef.current}chunks.webm`;
        
        logger.debug(`[VoiceRecorder] Created blob: ${blob.size} bytes`);
        
        // Reset local chunks for next recording
        localChunksRef.current = [];
        
        // Pass blob and session metadata through the callback
        onSave(blob, url, name);
      };

      // Start recording with 30-second chunks
      recorder.start(CHUNK_INTERVAL_MS);
      setRecording(true);
      
      logger.debug(`[VoiceRecorder] Recording started. Session ID: ${sessionId}`);
    } catch (error) {
      logger.error('[VoiceRecorder] Failed to start recording:', error);
      throw error;
    }
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      rec.stop();
    }
  };

  // Discard the current recording WITHOUT transcribing (true cancel). The
  // onstop handler sees cancelledRef and skips the upload-wait + onSave.
  const cancelRecording = () => {
    cancelledRef.current = true;
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      rec.stop();
    } else {
      cancelledRef.current = false;
      setRecording(false);
    }
  };

  // Cleanup if component unmounts mid-recording
  useEffect(() => {
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {}
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Backward compatible + enhanced
  return {
    recording,
    amplitude,
    recordingDuration,
    chunksRecorded,
    uploadProgress: uploadQueue.progress,
    startRecording,
    stopRecording,
    cancelRecording,
    streamRef,
  };
}
